/**
 * Shared on-demand credit pricing config.
 *
 * Imported by BOTH client (billing page summary) and server (checkout action,
 * webhook).  Keep all pricing logic here so the displayed summary always
 * matches what the server will actually charge.
 */

/**
 * USD price for a single execution credit before any tier discount.
 * Set this so the preset tiers ($50 / $250 / $1000) yield round credit counts.
 *
 * Default: $0.10 → 500 / 2,500 / 10,000 credits respectively.
 */
const ONDEMAND_UNIT_PRICE_USD = 0.1;

/**
 * LLM tokens granted per USD of on-demand purchase.
 * Default: 1,000,000 tokens per dollar
 *   → $50 = 50M tokens, $1000 = 1B tokens.
 *
 * These tokens are drawn down automatically when the user's monthly plan
 * token quota (`features.tokens_per_month`) is exhausted.
 */
export const ONDEMAND_TOKENS_PER_USD = 1_000_000;

/**
 * Preset USD amounts.  Each tier may grant a discount on the charge — the
 * user still receives `amountUsd / unit_price` credits, but pays less.
 *
 *   amountUsd: how much credit the user receives (in dollars at full unit price)
 *   discountPct: percentage off the charge (currently 0 — re-enable when ready)
 */
export const ONDEMAND_TIERS = [
  { amountUsd: 50, discountPct: 0 },
  { amountUsd: 250, discountPct: 0 },
  { amountUsd: 1000, discountPct: 0 },
] as const;

/** Minimum custom amount (Stripe Checkout has a $0.50 USD minimum). */
export const ONDEMAND_MIN_AMOUNT_USD = 5;

/** Soft cap on custom amounts to prevent UI typos. */
export const ONDEMAND_MAX_AMOUNT_USD = 10_000;

export interface OnDemandPricing {
  /** Face-value USD amount the user is buying credits worth of. */
  amountUsd: number;
  /** Number of execution credits to be delivered. */
  credits: number;
  /** Number of LLM tokens to be delivered. */
  tokens: number;
  /** `tokens` expressed in the user-facing credit unit (1 credit = 1k tokens). */
  llmCredits: number;
  /** Discount percentage applied to the charge (0 if no matching tier). */
  discountPct: number;
  /** USD savings vs face value. */
  savingsUsd: number;
  /** Final USD charged to the customer. */
  chargeUsd: number;
}

/**
 * Calculate pricing for a given USD amount.  Tiered discounts apply only to
 * exact preset matches; custom amounts pay full price.
 *
 * Do NOT round `amountUsd` here -- it is what Stripe charges and what the
 * token grant is derived from.  Whole cents x 1,000,000 tokens/$ is always a
 * multiple of 1,000, so credits come out whole on their own; display-side
 * flooring lives in `formatCredits`.
 */
export function calculateOnDemandPricing(amountUsd: number): OnDemandPricing {
  const tier = ONDEMAND_TIERS.find((t) => t.amountUsd === amountUsd);
  const discountPct = tier?.discountPct ?? 0;
  const credits = Math.floor(amountUsd / ONDEMAND_UNIT_PRICE_USD);
  const tokens = Math.floor(amountUsd * ONDEMAND_TOKENS_PER_USD);
  const savingsUsd = round2(amountUsd * (discountPct / 100));
  const chargeUsd = round2(amountUsd - savingsUsd);
  return {
    amountUsd,
    credits,
    tokens,
    llmCredits: tokensToCredits(tokens),
    discountPct,
    savingsUsd,
    chargeUsd,
  };
}

/**
 * Format a large token count compactly (e.g. 1,500,000 → "1.5M").
 *
 * @deprecated User-facing surfaces show credits, not tokens — use
 * `formatCredits`.  Kept for any remaining internal/diagnostic display.
 */
export function formatTokens(count: number | bigint): string {
  const n = typeof count === "bigint" ? Number(count) : count;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

/**
 * Display unit: 1 credit = 1,000 tokens ($1 = 1,000 credits = 1M tokens).
 *
 * Tokens remain the stored/metered unit everywhere — the plan catalog, the
 * Redis quota hash, `onDemandLLMTokens` and the Stripe grant are all in
 * tokens.  Credits exist only at the render boundary.
 */
const TOKENS_PER_CREDIT = 1000;

/**
 * Floor a token count to whole credits.  Flooring (rather than rounding) means
 * we never show a credit the user cannot actually spend.
 *
 * The `-1` unlimited sentinel passes through untouched.
 *
 * Strings are accepted because token counts routinely reach the render boundary
 * as Postgres `numeric`/`bigint` sums, which arrive as JS strings.  Coercing
 * before the guards matters: `Number.isFinite('1000')` is `false` (it does not
 * coerce), so an uncoerced string used to silently render as 0 credits.
 */
export function tokensToCredits(
  tokens: number | bigint | string | null | undefined,
): number {
  if (tokens === null || tokens === undefined) return 0;
  const n = Number(tokens);
  if (n === -1) return -1;
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n / TOKENS_PER_CREDIT));
}

/** Inverse of {@link tokensToCredits}, for admin inputs typed in credits. */
export function creditsToTokens(credits: number): number {
  if (credits === -1) return -1;
  if (!Number.isFinite(credits)) return 0;
  return Math.max(0, Math.round(credits)) * TOKENS_PER_CREDIT;
}

/**
 * Format a token count as whole credits — never abbreviated, never decimal.
 * e.g. 10,000,000 tokens → `"10,000"`; `-1` → `"∞"`.
 */
export function formatCredits(
  tokens: number | bigint | string | null | undefined,
): string {
  const c = tokensToCredits(tokens);
  return c === -1 ? "∞" : c.toLocaleString("en-US");
}

/** Round to 2 decimals to avoid float drift in the displayed total. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Format a USD amount for display (e.g. `$1,234.56`). */
export function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
